// src/quality-management/components/DeliveryTimeline.tsx
// Timeline estilo "Delivery Plan": barras de tarefa de task_date a due_date,
// agrupadas por projeto ou colaborador, coloridas pelo estado de cronograma.

import React, { useMemo } from 'react';
import { Task } from '../services/api/types';
import { taskScheduleState, getTaskRange, SCHEDULE_STYLES, startOfDay } from '../utils/taskSchedule';

const DAY = 86_400_000;
const LANE_H = 30; // altura de cada faixa (px)
const ROW_PAD = 8;

interface TimelineGroup {
  key: string;
  label: string;
  tasks: Task[];
}

interface DeliveryTimelineProps {
  tasks: Task[];
  groupBy: 'project' | 'employee';
  rangeStart: Date;
  rangeEnd: Date;
  onTaskClick?: (task: Task) => void;
}

// Empacota tarefas em faixas para que itens sobrepostos no tempo não colidam
function packLanes(tasks: Task[]): Task[][] {
  const withRange = tasks
    .map((t) => ({ t, r: getTaskRange(t) }))
    .filter((x) => x.r.start && x.r.end)
    .sort((a, b) => a.r.start!.getTime() - b.r.start!.getTime());

  const lanes: { end: number; items: Task[] }[] = [];
  for (const { t, r } of withRange) {
    const start = r.start!.getTime();
    const end = r.end!.getTime();
    let placed = false;
    for (const lane of lanes) {
      if (start > lane.end) {
        lane.items.push(t);
        lane.end = end;
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push({ end, items: [t] });
  }
  return lanes.map((l) => l.items);
}

const DeliveryTimeline: React.FC<DeliveryTimelineProps> = ({
  tasks,
  groupBy,
  rangeStart,
  rangeEnd,
  onTaskClick,
}) => {
  const now = useMemo(() => new Date(), []);
  const rangeStartMs = startOfDay(rangeStart).getTime();
  const rangeEndMs = startOfDay(rangeEnd).getTime() + DAY; // fim exclusivo
  const totalMs = Math.max(DAY, rangeEndMs - rangeStartMs);

  const pct = (ms: number) => ((ms - rangeStartMs) / totalMs) * 100;

  // Agrupa as tarefas
  const groups = useMemo<TimelineGroup[]>(() => {
    const map = new Map<string, TimelineGroup>();
    const add = (key: string, label: string, task: Task) => {
      if (!map.has(key)) map.set(key, { key, label, tasks: [] });
      map.get(key)!.tasks.push(task);
    };
    tasks.forEach((t) => {
      if (groupBy === 'project') {
        const id = t.project?.id ?? t.project_id;
        add(`p-${id}`, t.project?.name || `Projeto ${id}`, t);
      } else {
        const emps = t.employees && t.employees.length > 0 ? t.employees : [{ id: -1, name: 'Sem responsável' }];
        emps.forEach((e) => add(`e-${e.id}`, e.name, t));
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [tasks, groupBy]);

  // Marcas de mês dentro do intervalo
  const monthTicks = useMemo(() => {
    const ticks: { left: number; label: string }[] = [];
    const d = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    // avança até o primeiro mês visível
    while (d.getTime() < rangeStartMs) d.setMonth(d.getMonth() + 1);
    while (d.getTime() <= rangeEndMs) {
      ticks.push({
        left: Math.max(0, pct(d.getTime())),
        label: d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
      });
      d.setMonth(d.getMonth() + 1);
    }
    return ticks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartMs, rangeEndMs]);

  const todayLeft = pct(startOfDay(now).getTime());
  const todayVisible = todayLeft >= 0 && todayLeft <= 100;

  if (groups.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-border bg-surface-2 text-sm text-foreground-muted">
        Nenhuma tarefa com datas no período selecionado.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface-1" data-testid="delivery-timeline">
      {/* Cabeçalho de meses */}
      <div className="flex border-b border-border bg-surface-2">
        <div className="w-44 shrink-0 border-r border-border px-4 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-muted">
          {groupBy === 'project' ? 'Projeto' : 'Colaborador'}
        </div>
        <div className="relative h-8 flex-1">
          {monthTicks.map((t, i) => (
            <div
              key={i}
              className="absolute top-0 flex h-full items-center pl-1 text-[11px] font-medium uppercase text-foreground-subtle"
              style={{ left: `${t.left}%` }}
            >
              <span className="border-l border-border pl-1">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Linhas por grupo */}
      <div>
        {groups.map((group) => {
          const lanes = packLanes(group.tasks);
          const rowHeight = Math.max(LANE_H, lanes.length * LANE_H) + ROW_PAD * 2;
          return (
            <div key={group.key} className="flex border-b border-border last:border-0">
              <div className="flex w-44 shrink-0 items-center border-r border-border px-4 py-2">
                <span className="truncate text-sm font-medium text-foreground" title={group.label}>
                  {group.label}
                </span>
              </div>
              <div className="relative flex-1" style={{ height: rowHeight }}>
                {/* gridlines de mês */}
                {monthTicks.map((t, i) => (
                  <div
                    key={i}
                    className="absolute top-0 h-full border-l border-border/50"
                    style={{ left: `${t.left}%` }}
                  />
                ))}
                {/* marcador de hoje */}
                {todayVisible && (
                  <div
                    className="absolute top-0 z-10 h-full border-l-2 border-brand/70"
                    style={{ left: `${todayLeft}%` }}
                    aria-label="Hoje"
                  />
                )}
                {/* barras */}
                {lanes.map((lane, laneIndex) =>
                  lane.map((task) => {
                    const { start, end } = getTaskRange(task);
                    if (!start || !end) return null;
                    const startMs = start.getTime();
                    const endMs = end.getTime() + DAY;
                    if (endMs <= rangeStartMs || startMs >= rangeEndMs) return null; // fora do período
                    const left = Math.max(0, pct(startMs));
                    const right = Math.min(100, pct(endMs));
                    const width = Math.max(1.2, right - left);
                    const state = taskScheduleState(task, now);
                    const style = SCHEDULE_STYLES[state];
                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => onTaskClick?.(task)}
                        data-testid="timeline-bar"
                        title={`${task.name} • ${style.label}${end ? ` • até ${end.toLocaleDateString('pt-BR')}` : ''}`}
                        className={`absolute flex items-center overflow-hidden rounded-md px-2 text-left text-xs font-medium text-white shadow-sm ring-1 ring-inset ${style.bar} ${style.ring} transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
                        style={{
                          left: `${left}%`,
                          width: `${width}%`,
                          top: ROW_PAD + laneIndex * LANE_H,
                          height: LANE_H - 6,
                        }}
                      >
                        <span className="truncate">{task.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryTimeline;
